import supabase from '../supabaseClient'
import { TABLE_NAMES } from '../config'
import { COMMUNITY_JOIN_BASE_URL } from './communityInvite'

/** sessionStorage key — survives OAuth redirect until join completes */
export const ACADEMY_JOIN_STORAGE_KEY = 'statbox:academy:joinId'

const ACADEMY_INVITE_PATH = '/academy/invite'
const PRODUCTION_ORIGIN = 'https://www.statbox.co'

/** Academy coach invite path — web-only landing + sign-in flow (not a mobile universal link). */
export function getAcademyJoinBaseUrl() {
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    return `${window.location.origin}${ACADEMY_INVITE_PATH}`
  }
  return `${PRODUCTION_ORIGIN}${ACADEMY_INVITE_PATH}`
}

export function buildAcademyJoinUrl(academyId) {
  if (!academyId) return ''
  return `${getAcademyJoinBaseUrl()}/${academyId}`
}

/** Player roster invite — universal link path matches communities (/join/<academy_id_join>). */
export function buildAcademyPlayerJoinUrl(academyJoinToken) {
  if (!academyJoinToken) return ''
  return `${COMMUNITY_JOIN_BASE_URL}/${academyJoinToken}`
}

export function buildAcademyInviteWhatsAppText(academyName, joinUrl) {
  const name = academyName || 'our academy'
  return `Join ${name} on Statbox! Tap this link to join as a coach: ${joinUrl}`
}

export function buildAcademyOwnerInviteWhatsAppText(academyName, joinUrl) {
  const name = academyName || 'our academy'
  return `You're invited to own ${name} on Statbox. Tap this link to sign in and become the primary owner: ${joinUrl}`
}

export function buildAcademyPlayerInviteWhatsAppText(academyName, joinUrl) {
  const name = academyName || 'our academy'
  return `Join ${name} on Statbox! Tap this link to join as a player: ${joinUrl}`
}

export function stashAcademyJoinId(academyId) {
  if (typeof window === 'undefined' || !academyId) return
  sessionStorage.setItem(ACADEMY_JOIN_STORAGE_KEY, academyId)
}

export function peekAcademyJoinId() {
  if (typeof window === 'undefined') return null
  return sessionStorage.getItem(ACADEMY_JOIN_STORAGE_KEY) || null
}

export function clearAcademyJoinId() {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(ACADEMY_JOIN_STORAGE_KEY)
}

/** Attach coach to academy by id (invite link flow). */
export async function joinAcademyAsCoach(userId, academyId) {
  if (!userId || !academyId) {
    return { ok: false, error: 'Missing user or academy.' }
  }

  const { data: academy, error: fetchError } = await supabase
    .from(TABLE_NAMES.ACADEMIES)
    .select('id, name')
    .eq('id', academyId)
    .maybeSingle()

  if (fetchError || !academy) {
    return { ok: false, error: 'Academy not found.' }
  }

  const { error: updateError } = await supabase
    .from(TABLE_NAMES.USERS)
    .update({ academy_id: academy.id, coach: 'true' })
    .eq('user_id', userId)

  if (updateError) {
    return { ok: false, error: 'Could not join academy.' }
  }

  const ownership = await claimAcademyOwnershipIfUnowned(userId, academyId)
  if (!ownership.ok) {
    return { ok: false, error: ownership.error || 'Could not assign academy owner.' }
  }

  return { ok: true, academy, ownerClaimed: ownership.claimed }
}

/** First person through an owner invite becomes academies.created_by. Later joins stay coaches. */
export async function claimAcademyOwnershipIfUnowned(userId, academyId) {
  if (!userId || !academyId) {
    return { ok: false, error: 'Missing user or academy.' }
  }

  const { data: academy, error: fetchError } = await supabase
    .from(TABLE_NAMES.ACADEMIES)
    .select('created_by')
    .eq('id', academyId)
    .maybeSingle()

  if (fetchError) {
    return { ok: false, error: fetchError.message || 'Could not load academy.' }
  }
  if (!academy || academy.created_by) {
    return { ok: true, claimed: false }
  }

  const { error: updateError } = await supabase
    .from(TABLE_NAMES.ACADEMIES)
    .update({ created_by: userId })
    .eq('id', academyId)
    .is('created_by', null)

  if (updateError) {
    return { ok: false, error: updateError.message || 'Could not assign academy owner.' }
  }

  await supabase
    .from(TABLE_NAMES.USERS)
    .update({ academy_owner: 'true' })
    .eq('user_id', userId)

  return { ok: true, claimed: true }
}

export function academyInviteSignInPath(academyId) {
  if (!academyId) return '/academy/login'
  return `/academy/invite/${academyId}/sign-in`
}

/** Public preview fields for the coach invite landing + sign-in pages. */
export async function fetchAcademyInvitePreview(academyId) {
  if (!academyId) return null
  try {
    const { data, error } = await supabase
      .from(TABLE_NAMES.ACADEMIES)
      .select('id, name, logo_url, Sport')
      .eq('id', academyId)
      .maybeSingle()
    if (error || !data) return null
    return {
      id: data.id,
      name: data.name || 'Academy',
      logoUrl: data.logo_url || null,
      sport: String(data.Sport || 'Football').trim() || 'Football',
    }
  } catch {
    return null
  }
}
